<template>
  <div class="form-page">
    <Header />
    <div class="login-form">
      <Card>
        <div v-if="!isShowThankYou" class="mb-3 text-center">
          <h2 class="font-size-20 font-weight-700 text-title-color">Welcome to Pension Keeper</h2>
          <h2 class="font-size-16 text-title-color">We need to ask you some questions to setup your file.</h2>
        </div>
        <div v-if="isLoadingForm" class="d-flex flex-column align-center justify-center py-4 w-100">
          <VProgressCircular indeterminate color="primary" />
        </div>
        <VForm v-else-if="isFoundForm" v-model="formValid" @submit.prevent>
          <VRow v-if="!isShowThankYou">
            <VCol cols="12">
              <div class="review-input">
                <div class="review-input__label">Application No</div>
                <div class="review-input__value">{{ form.title }}</div>
              </div>
            </VCol>
            <VCol cols="12">
              <div class="review-input">
                <div class="review-input__label">Full Name</div>
                <div class="review-input__value">{{ applicationFullName }}</div>
              </div>
            </VCol>
            <VCol cols="12">
              <div class="review-input">
                <div class="review-input__label">Phone</div>
                <div class="review-input__value">{{ form.phone }}</div>
              </div>
            </VCol>
            <VCol cols="12">
              <div class="review-input">
                <div class="review-input__label">Email</div>
                <div class="review-input__value">{{ form.email }}</div>
              </div>
            </VCol>
            <VCol cols="12">
              <div class="review-input">
                <div class="review-input__label">Current Address</div>
                <div class="review-input__value">{{ applicationFullAddress }}</div>
              </div>
            </VCol>
            <VCol cols="12">
              <VTextField
                label="National Insurance Number"
                prepend-inner-icon="mdi-account-box-outline"
                variant="outlined"
                color="primary"
                name="nino"
                :rules="formRules.nino"
                v-model="form.nino"
                placeholder="e.g. AB123456C"
                :disabled="isLoading"
                type="text"
                @input="formatNino"
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                type="date"
                label="Date of Birth"
                v-model="form.dateOfBirth"
                :rules="formRules.dateOfBirth"
                variant="outlined"
                persistent-placeholder
                prepend-inner-icon="mdi-calendar-clock"
                prepend-icon=""
                :disabled="isLoading"
                :max="maxDate"
                input-format="dd/mm/yyyy"
              ></VTextField>
            </VCol>
            <VCol cols="12">
              <VSelect
                label="How many qualifying years you have in the UK?"
                v-model="form.ukYears"
                :items="ukYearsOptions"
                :rules="formRules.ukYears"
                variant="outlined"
                prepend-inner-icon="mdi-calendar-clock"
                :disabled="isLoading"
                placeholder="Select years in UK"
              >
                <template v-slot:append>
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" icon="mdi-information" class="mr-2" style="cursor: help"></v-icon>
                    </template>
                    <span
                      >Your qualifying years in the UK determine your eligibility for certain benefits and state
                      pension</span
                    >
                  </v-tooltip>
                </template>
              </VSelect>
            </VCol>
            <VCol cols="12">
              <div>Please scan and upload your approval letter.</div>
            </VCol>
            <VCol cols="12">
              <VFileUpload v-model="filesToUpload" clearable show-size :rules="[fileSizeRule]" :disabled="isLoading">
                <template v-slot:item="{ props: itemProps }">
                  <VFileUploadItem v-bind="itemProps" lines="one" nav>
                    <template v-slot:prepend>
                      <VAvatar size="32" rounded></VAvatar>
                    </template>

                    <template v-slot:clear="{ props: clearProps }">
                      <VBtn color="primary" v-bind="clearProps"></VBtn>
                    </template>
                  </VFileUploadItem>
                </template>
              </VFileUpload>
            </VCol>
            <VCol cols="12">
              <VBtn :loading="isLoading" type="submit" color="primary" block flat @click="handleSave()">Save</VBtn>
            </VCol>
          </VRow>
          <ThankYou v-else>
            <template v-slot:title>
              <span class="text-main-color">Thanks!</span> We’re preparing your file <br />
              and will share your login details soon.
            </template>
            <template v-slot:text>
              You should get an email within the next hour with your <br />
              Pension Keeper file. <i>(Check your spam folder too)</i>.
            </template>
          </ThankYou>
        </VForm>
        <div v-else class="info-container info-container__red">This link is not valid.</div>
      </Card>
    </div>
  </div>
</template>
<script>
import { mapActions } from 'vuex';
// Service
import applicationsService from '@/services/applications-service/applications.service';
// Utils
import { formatFormApplication } from '@/utils/formatData';
import { fileToBase64 } from '@/utils/convertBlobToBase64';
// Components
import Card from '@/components/common/Card/Card.vue';
import Header from '@/components/common/Header/Header.vue';
import { VFileUpload } from 'vuetify/lib/labs/VFileUpload';
import { VFileUploadItem } from 'vuetify/lib/labs/VFileUpload';
import ThankYou from '@/components/common/ThankYou/ThankYou.vue';
export default {
  name: 'FormPage',
  components: {
    Header,
    Card,
    VFileUpload,
    VFileUploadItem,
    ThankYou
  },
  computed: {
    applicationFullName() {
      return this.form.firstName + ' ' + this.form.lastName;
    },
    applicationFullAddress() {
      const addressParts = [
        this.form.currentAddress?.street,
        this.form.currentAddress?.city,
        this.form.currentAddress?.state,
        this.form.currentAddress?.postalCode,
        this.form.currentAddress?.country
      ].filter((part) => part && part.trim() !== '');

      return addressParts.length > 0 ? addressParts.join(', ') : 'Address not provided';
    },
    maxDate() {
      const today = new Date();
      const minAgeDate = new Date(today.setFullYear(today.getFullYear() - 16));
      return minAgeDate.toISOString().split('T')[0];
    },
    ukYearsOptions() {
      return Array.from({ length: 34 }, (_, i) => ({
        value: i + 1,
        title: `${i + 1} ${i + 1 === 1 ? 'year' : 'years'}`
      }));
    }
  },
  data() {
    return {
      filesToUpload: [],
      isLoadingForm: true,
      isFoundForm: false,
      isShowThankYou: false,
      formRules: {
        nino: [
          (v) => !!v || 'National Insurance Number is required',
          (v) => this.validateNino(v) || 'Please enter a valid UK National Insurance Number (e.g., AB123456C)'
        ],
        dateOfBirth: [
          (v) => !!v || 'Date of Birth is required',
          (v) => this.validatePastDate(v) || 'Date of Birth must be in the past',
          (v) => this.validateMinAge(v, 16) || 'You must be at least 16 years old'
        ],
        ukYears: [(v) => (v !== null && v !== undefined && v !== '') || 'UK Years is required']
      },
      fileSizeRule: (files) => {
        if (!files || files.length === 0) return true;

        const maxSize = 5 * 1024 * 1024;

        for (const f of files) {
          if (f.size > maxSize) {
            return `File "${f.name}" exceeds 5 MB limit`;
          }
        }

        return true;
      },
      form: {
        id: '',
        firstName: '',
        lastName: '',
        title: '',
        email: '',
        phone: '',
        nino: '',
        ukYears: null,
        dateOfBirth: '',
        currentAddress: {
          street: '',
          state: '',
          postalCode: '',
          country: '',
          city: '',
          countryCode: ''
        }
      },
      isLoading: false,
      formValid: false
    };
  },
  mounted() {
    if (this.$route?.params?.id) {
      this.initForm();
    } else this.$router.push({ name: 'Login' });
  },
  methods: {
    ...mapActions('main', ['showSnackbar']),
    ...mapActions('user', ['logInUser']),

    validateNino(nino) {
      if (!nino) return false;
      const ninoRegex = /^(?!BG|GB|NK|KN|TN|NT|ZZ)[A-CEGHJ-PR-TW-Z]{2}[0-9]{6}[A-D]{1}$/;
      const cleanNino = nino.replace(/\s/g, '').toUpperCase();

      return ninoRegex.test(cleanNino);
    },

    formatNino(event) {
      let value = event.target.value.replace(/\s/g, '').toUpperCase();
      if (value.length > 0) {
        const parts = [];
        if (value.length > 2) {
          parts.push(value.substring(0, 2));
          if (value.length > 4) {
            parts.push(value.substring(2, 4));
            if (value.length > 6) {
              parts.push(value.substring(4, 6));
              if (value.length > 8) {
                parts.push(value.substring(6, 8));
                parts.push(value.substring(8, 9));
              } else {
                parts.push(value.substring(6));
              }
            } else {
              parts.push(value.substring(4));
            }
          } else {
            parts.push(value.substring(2));
          }
        } else {
          parts.push(value);
        }
        this.form.nino = parts.join(' ');
      }
    },

    validatePastDate(date) {
      if (!date) return false;
      const selectedDate = new Date(date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return selectedDate < today;
    },

    validateMinAge(date, minAge) {
      if (!date) return false;
      const selectedDate = new Date(date);
      const today = new Date();
      const minAgeDate = new Date(today.setFullYear(today.getFullYear() - minAge));
      return selectedDate <= minAgeDate;
    },

    async initForm() {
      this.isLoadingForm = true;
      try {
        const { application } = await applicationsService.getPkApplication({ id: this.$route.params.id });
        this.form = formatFormApplication(application);
        this.isFoundForm = true;
      } catch (err) {
        console.error(err);
      } finally {
        this.isLoadingForm = false;
      }
    },
    async handleSave() {
      if (this.formValid === true) {
        this.isLoading = true;
        try {
          let document = {
            base64: '',
            fileType: '',
            fileName: '',
            recordId: this.form.id
          };
          if (this.filesToUpload?.length) {
            const file = this.filesToUpload[0];
            const { base64, fileType } = await fileToBase64(file);
            document = {
              base64: base64,
              fileType: fileType,
              fileName: file.name,
              recordId: this.form.id
            };
          }
          await applicationsService.savePkApplication({ application: this.form, document: document });
          this.isShowThankYou = true;
        } catch (err) {
          console.error(err);
          this.showSnackbar({ text: 'Oops, Something Went Wrong', color: 'red' });
        } finally {
          this.isLoading = false;
        }
      }
    }
  }
};
</script>
